import cx from "classnames";
import { Flex, Text, Button, List, type ListItemOption } from "@theorgchartdev/storybook";
import { appstr } from "@coreapp/utils/appstr";
import ActionListItem from "@/appComponents/Blocks/Composites/ActionListItem/ActionListItem";
import UpgradePopover, {
  PopoverText
} from "@/appComponents/Blocks/Composites/UpgradePopover/UpgradePopover";
import { getFeatureAccess } from "@/Utils/Misc";
import { enumFeaturesList } from "@coreapp/constants";
import { enumRuleSets } from "../RowLevelSecurityModal.utils";

interface RuleOptionsSectionProps {
  selectedRule: string;
  conditionalFields: any[];
  selectedConditionalRule: string;
  changeRuleSet: (set: string, conditionalIndex?: string) => void;
  AddConditionalRule: () => void;
  onConditionalRuleChange: (action: string, conditionalIndex: string) => void;
  showPricingAndPackaging: boolean;
}

export const RuleOptionsSection = ({
  selectedRule,
  conditionalFields,
  selectedConditionalRule,
  changeRuleSet,
  AddConditionalRule,
  onConditionalRuleChange,
  showPricingAndPackaging
}: RuleOptionsSectionProps) => {
  const BuildCurrentRules = () => {
    let rules = [];
    let allowedOption: ListItemOption = {
      value: enumRuleSets.allowed,
      label: appstr("Allowed fields"),
      clickHandler: () => changeRuleSet(enumRuleSets.allowed),
      dataTestId: "account-settings-security-rls-rule-set-allowed-option"
    };

    rules.push(allowedOption);

    let restrictedOption: ListItemOption = {
      value: enumRuleSets.restricted,
      label: appstr("Restricted fields"),
      clickHandler: () => changeRuleSet(enumRuleSets.restricted),
      dataTestId: "account-settings-security-rls-rule-set-restricted-option"
    };

    rules.push(restrictedOption);

    let conditionalFieldsList = conditionalFields || [];

    for (let i in conditionalFieldsList) {
      let id = parseInt(i) + 1;
      let option: ListItemOption = {
        value: i,
        clickHandler: () => changeRuleSet(enumRuleSets.conditional, i),
        content: (
          <ActionListItem
            dataTestId={`rls-modal-conditional-rule-${id}`}
            label={appstr("Conditional rule") + " " + id.toString()}
            labelProps={{
              size: "small",
              className: i == selectedConditionalRule ? "ocn-file-row-active-text" : undefined
            }}
            actions={[
              {
                iconName: "copy",
                dataTestId: `rls-modal-conditional-rule-${id}--copy`,
                onClick: () => onConditionalRuleChange("copy", i),
                disabled: !getFeatureAccess(enumFeaturesList.conditionalRLS)
              },
              {
                iconName: "trash-can",
                iconProps: { color: "error" },
                dataTestId: `rls-modal-conditional-rule-${id}--delete`,
                onClick: () => onConditionalRuleChange("remove", i)
              }
            ]}
          />
        )
      };

      rules.push(option);
    }
    return rules;
  };

  const upgradeMessaging: PopoverText = {
    message: appstr("conditional rls message"),
    template: appstr("conditional rls template"),
    formatConfig: {
      bold1: { type: "bold" }
    },
    replaceDict: {
      bold1: appstr("conditional row level")
    }
  };

  const currentRules = BuildCurrentRules();

  return (
    <Flex direction={"column"} gap={"4px"} width={"100%"} height={"100%"}>
      <Flex justifyContent={"space-between"} gap={"4px"} alignItems={"center"} width={"100%"}>
        <Text modifier={"bold"} size={"medium"}>
          {appstr("Rules")}
        </Text>
        <Flex className={"oc-right-justify"} padding={"0 4px 0 0"}>
          {getFeatureAccess(enumFeaturesList.conditionalRLS) && (
            <Button
              size={"medium"}
              className={cx("oc-special-secondary", "oc-special-secondary--fit")}
              modifier={"secondary"}
              onClick={AddConditionalRule}
              tooltipProps={{ message: appstr("Create new conditional rule") }}
              iconProps={{ name: "plus" }}
              dataTestId="rls-modal-add-conditional-rule"
              disabled={!getFeatureAccess(enumFeaturesList.conditionalRLS)}
            >
              {appstr("Conditional rule")}
            </Button>
          )}
          {showPricingAndPackaging && !getFeatureAccess(enumFeaturesList.conditionalRLS) && (
            <UpgradePopover
              pillLabel={appstr("Upgrade to create")}
              popoverText={upgradeMessaging}
              featureKey={enumFeaturesList.conditionalRLS}
            />
          )}
        </Flex>
      </Flex>
      <div
        className={cx(
          "oc-list-container",
          "ocn-list-scrollbar",
          "oc-list-container--divider-right"
        )}
      >
        <List
          enableVirtualization={false}
          coloredSelection={true}
          showCheck={true}
          value={selectedRule === enumRuleSets.conditional ? selectedConditionalRule : selectedRule}
          optionSize={"medium"}
          options={currentRules}
          listWidth="fill"
        />
      </div>
    </Flex>
  );
};
